import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { 
  FaCheck, 
  FaTimes, 
  FaArrowRight, 
  FaStar, 
  FaFire,
  FaTrophy,
  FaHeart,
  FaLightbulb,
  FaVolumeUp,
  FaRedo,
  FaPause,
  FaPlay
} from 'react-icons/fa';

const LXDLesson = () => {
  const { id, lessonId } = useParams();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [hearts, setHearts] = useState(5);
  const [xp, setXp] = useState(0);
  const [streak, setStreak] = useState(0);
  const [lessonComplete, setLessonComplete] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [showMascot, setShowMascot] = useState(false);
  const [mascotMessage, setMascotMessage] = useState('');
  const [combo, setCombo] = useState(0);
  const [showCombo, setShowCombo] = useState(false);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [level, setLevel] = useState(1);

  const questions = [
    {
      type: 'multiple-choice',
      question: 'What does HTML stand for?',
      options: [
        'Hyper Text Markup Language',
        'High Tech Modern Language',
        'Hyper Transfer Markup Language',
        'Home Tool Markup Language'
      ],
      correct: 0,
      hint: 'Think about the purpose of HTML in web development',
      explanation: 'HTML stands for Hyper Text Markup Language, which is the standard markup language for creating web pages.'
    },
    {
      type: 'fill-blank',
      question: 'Complete the sentence: React is a _______ library for building user interfaces.',
      options: ['JavaScript', 'Python', 'Java', 'C++'],
      correct: 0,
      hint: 'React is commonly used with web technologies',
      explanation: 'React is a JavaScript library developed by Facebook for building user interfaces.'
    },
    {
      type: 'matching',
      question: 'Match the CSS property with its correct value:',
      pairs: [
        { property: 'color', value: 'red' },
        { property: 'font-size', value: '16px' },
        { property: 'background', value: 'blue' }
      ],
      hint: 'Think about what each property controls',
      explanation: 'CSS properties control different aspects of element styling.'
    },
    {
      type: 'translation',
      question: 'Translate this code to English: console.log("Hello World")',
      options: [
        'Print "Hello World" to the console',
        'Save "Hello World" to a variable',
        'Create a function called Hello World',
        'Delete "Hello World" from memory'
      ],
      correct: 0,
      hint: 'console.log is used for debugging',
      explanation: 'console.log() is used to output messages to the browser console for debugging purposes.'
    },
    {
      type: 'listening',
      question: 'What does this code do?',
      code: 'const x = 5;\nconst y = 10;\nconst sum = x + y;',
      options: [
        'Adds 5 and 10',
        'Multiplies 5 and 10',
        'Subtracts 10 from 5',
        'Divides 10 by 5'
      ],
      correct: 0,
      hint: 'Look at the operator used',
      explanation: 'The + operator performs addition, so sum equals 15.'
    }
  ];

  const handleAnswerSelect = (index) => {
    if (showFeedback) return;
    
    setSelectedAnswer(index);
    setShowFeedback(true);
    
    const question = questions[currentQuestion];
    const correct = question.type === 'matching' 
      ? index === question.correct 
      : index === question.correct;
    
    setIsCorrect(correct);
    
    if (correct) {
      setXp(prev => prev + 10);
      setStreak(prev => prev + 1);
      setCombo(prev => prev + 1);
      setShowCombo(true);
      
      // Check for level up (every 50 XP)
      if ((xp + 10) % 50 === 0) {
        setLevel(prev => prev + 1);
        setShowLevelUp(true);
        setTimeout(() => setShowLevelUp(false), 3000);
      }
      
      // Random encouraging message
      const correctMessages = [
        "Excellent! You're doing great! 🎉",
        "Perfect! Keep up the amazing work! ⭐",
        "Wow! You're a natural at this! 🚀",
        "Fantastic! You're learning so fast! 💪",
        "Brilliant! That's exactly right! 🌟",
        "Amazing! You're on fire! 🔥",
        "Superb! You're crushing it! 💯",
        "Incredible! You're making great progress! 📈"
      ];
      setMascotMessage(correctMessages[Math.floor(Math.random() * correctMessages.length)]);
      
      setTimeout(() => setShowCombo(false), 1500);
    } else {
      setHearts(prev => prev - 1);
      setStreak(0);
      setCombo(0);
      // Encouraging message for wrong answer
      const incorrectMessages = [
        "No worries! Let's try again! 💪",
        "Almost there! You've got this! 🎯",
        "Don't give up! Practice makes perfect! 📚",
        "That's okay! Learning is a journey! 🌱",
        "Keep going! You'll get it next time! ✨",
        "No problem! Every mistake is a lesson! 🎓"
      ];
      setMascotMessage(incorrectMessages[Math.floor(Math.random() * incorrectMessages.length)]);
    }
    
    setShowMascot(true);
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
      setShowFeedback(false);
      setShowHint(false);
      setShowMascot(false);
      setShowCombo(false);
    } else {
      setLessonComplete(true);
    }
  };

  const handleRetry = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setHearts(5);
    setXp(0);
    setStreak(0);
    setCombo(0);
    setLevel(1);
    setLessonComplete(false);
    setShowHint(false);
    setShowMascot(false);
    setShowCombo(false);
    setShowLevelUp(false);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.3
      }
    }
  };

  if (lessonComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 pt-20">
        {/* Confetti Celebration */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden z-40">
          {[...Array(50)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ 
                x: Math.random() * window.innerWidth,
                y: -50,
                rotate: Math.random() * 360
              }}
              animate={{
                y: window.innerHeight + 50,
                rotate: Math.random() * 360 + 360,
                x: Math.random() * window.innerWidth
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                delay: Math.random() * 2,
                repeat: Infinity,
                repeatDelay: Math.random() * 3
              }}
              className="absolute text-2xl"
            >
              {['🎉', '🎊', '⭐', '✨', '🎈', '🦁', '💫', '🌟'][i % 8]}
            </motion.div>
          ))}
        </div>

        <div className="container mx-auto px-6 py-12 relative z-10">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="max-w-2xl mx-auto"
          >
            <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-2xl text-center relative overflow-hidden">
              {/* Animated Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-100 to-blue-100 opacity-50" />
              
              <div className="relative z-10">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.2, type: "spring", duration: 1 }}
                  className="w-32 h-32 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl"
                >
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                      rotate: [0, 10, -10, 0]
                    }}
                    transition={{
                      duration: 0.5,
                      repeat: Infinity
                    }}
                    className="text-6xl"
                  >
                    🦁
                  </motion.div>
                </motion.div>
                
                <motion.h1
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-5xl font-bold text-gray-800 mb-4"
                >
                  Lesson Complete! 🎉
                </motion.h1>
                
                <motion.p
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="text-2xl text-gray-600 mb-8"
                >
                  ROAR-some work! You've mastered this lesson! 🦁
                </motion.p>

                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="grid grid-cols-3 gap-4 mb-8"
                >
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-4 text-white shadow-lg">
                    <div className="text-3xl font-bold">{xp}</div>
                    <div className="text-sm opacity-90">XP Earned</div>
                  </div>
                  <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-4 text-white shadow-lg">
                    <div className="text-3xl font-bold">{streak}</div>
                    <div className="text-sm opacity-90">Streak</div>
                  </div>
                  <div className="bg-gradient-to-br from-red-500 to-pink-500 rounded-2xl p-4 text-white shadow-lg">
                    <div className="text-3xl font-bold">{hearts}</div>
                    <div className="text-sm opacity-90">Hearts Left</div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="flex gap-4 justify-center"
                >
                  <Link
                    to={`/courses/${id}`}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:opacity-90 transition-all shadow-lg flex items-center gap-2"
                  >
                    <FaArrowRight /> Continue Learning
                  </Link>
                  <button
                    onClick={handleRetry}
                    className="border-2 border-purple-600 text-purple-600 px-8 py-4 rounded-full font-semibold hover:bg-purple-50 transition-all flex items-center gap-2"
                  >
                    <FaRedo /> Try Again
                  </button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (hearts === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 pt-20">
        <div className="container mx-auto px-6 py-12">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="max-w-2xl mx-auto"
          >
            <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-2xl text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="w-24 h-24 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <FaHeart className="text-4xl text-white" />
              </motion.div>
              
              <h1 className="text-4xl font-bold text-gray-800 mb-4">
                Out of Hearts 💔
              </h1>
              
              <p className="text-xl text-gray-600 mb-8">
                Don't worry! Take a break and come back later to continue learning.
              </p>

              <div className="flex gap-4 justify-center">
                <Link
                  to={`/courses/${id}`}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:opacity-90 transition-all"
                >
                  Back to Course
                </Link>
                <button
                  onClick={handleRetry}
                  className="border-2 border-purple-600 text-purple-600 px-8 py-4 rounded-full font-semibold hover:bg-purple-50 transition-all"
                >
                  Try Again
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 pt-20">
      {/* Progress Bar */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to={`/courses/${id}`} className="text-gray-400 hover:text-gray-600">
              <FaTimes className="text-xl" />
            </Link>
            <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
                className="h-full bg-gradient-to-r from-purple-600 to-blue-600"
              />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FaHeart className="text-red-500" />
              <span className="font-semibold">{hearts}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="container mx-auto px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
              <FaFire className="text-orange-500" />
              <span className="font-semibold">{streak}</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
              <FaStar className="text-yellow-500" />
              <span className="font-semibold">{xp} XP</span>
            </div>
          </div>
          <button
            onClick={() => setShowHint(!showHint)}
            className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm hover:bg-gray-50 transition-all"
          >
            <FaLightbulb className="text-yellow-500" />
            <span className="font-semibold">Hint</span>
          </button>
        </div>
      </div>

      {/* Question Area */}
      <div className="container mx-auto px-6 py-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-3xl mx-auto"
        >
          <motion.div
            variants={itemVariants}
            className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl mb-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-purple-100 text-purple-600 px-3 py-1 rounded-full text-sm font-semibold">
                Question {currentQuestion + 1}
              </span>
              <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-semibold">
                {currentQ.type.replace('-', ' ')}
              </span>
            </div>

            <h2 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-6">
              {currentQ.question}
            </h2>

            {currentQ.code && (
              <div className="bg-gray-900 text-green-400 p-4 rounded-xl mb-6 font-mono text-sm">
                <pre>{currentQ.code}</pre>
              </div>
            )}

            <AnimatePresence>
              {showHint && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6"
                >
                  <div className="flex items-center gap-2 text-yellow-700 mb-2">
                    <FaLightbulb />
                    <span className="font-semibold">Hint</span>
                  </div>
                  <p className="text-yellow-800">{currentQ.hint}</p>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-3">
              {currentQ.options.map((option, index) => (
                <motion.button
                  key={index}
                  variants={itemVariants}
                  onClick={() => handleAnswerSelect(index)}
                  disabled={showFeedback}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    selectedAnswer === index
                      ? showFeedback
                        ? isCorrect
                          ? 'border-green-500 bg-green-50'
                          : 'border-red-500 bg-red-50'
                        : 'border-purple-500 bg-purple-50'
                      : showFeedback && index === currentQ.correct
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      selectedAnswer === index
                        ? showFeedback
                          ? isCorrect
                            ? 'bg-green-500 text-white'
                            : 'bg-red-500 text-white'
                          : 'bg-purple-500 text-white'
                        : showFeedback && index === currentQ.correct
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200'
                    }`}>
                      {showFeedback && (selectedAnswer === index || index === currentQ.correct) ? (
                        isCorrect || index === currentQ.correct ? (
                          <FaCheck />
                        ) : (
                          <FaTimes />
                        )
                      ) : (
                        <span className="text-sm font-semibold">{String.fromCharCode(65 + index)}</span>
                      )}
                    </div>
                    <span className="font-medium text-gray-800">{option}</span>
                  </div>
                </motion.button>
              ))}
            </div>

            <AnimatePresence>
              {showFeedback && (
                <>
                  {/* Combo Animation */}
                  {showCombo && combo > 1 && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0, y: -50 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0, y: -50 }}
                      className="fixed top-32 left-1/2 -translate-x-1/2 z-50 pointer-events-none"
                    >
                      <motion.div
                        animate={{
                          scale: [1, 1.2, 1],
                          rotate: [-5, 5, -5]
                        }}
                        transition={{
                          duration: 0.3,
                          repeat: Infinity
                        }}
                        className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-full shadow-2xl flex items-center gap-3"
                      >
                        <span className="text-3xl">🔥</span>
                        <span className="text-3xl font-bold">{combo}x COMBO!</span>
                        <span className="text-2xl">⚡</span>
                      </motion.div>
                    </motion.div>
                  )}

                  {/* Level Up Animation */}
                  <AnimatePresence>
                    {showLevelUp && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center pointer-events-none"
                      >
                        <motion.div
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          exit={{ scale: 0, rotate: 180 }}
                          transition={{ type: "spring", duration: 1 }}
                          className="bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 rounded-3xl p-12 shadow-2xl text-center"
                        >
                          <motion.div
                            animate={{
                              scale: [1, 1.3, 1],
                              rotate: [0, 10, -10, 0]
                            }}
                            transition={{
                              duration: 0.5,
                              repeat: Infinity
                            }}
                            className="text-8xl mb-4"
                          >
                            🎉
                          </motion.div>
                          <h2 className="text-4xl font-bold text-white mb-2">LEVEL UP!</h2>
                          <div className="text-6xl font-bold text-white mb-4">{level}</div>
                          <p className="text-white text-xl">You're getting stronger! 🦁</p>
                          {[...Array(20)].map((_, i) => (
                            <motion.div
                              key={i}
                              initial={{ 
                                x: '50%', 
                                y: '50%', 
                                scale: 0,
                                opacity: 1
                              }}
                              animate={{
                                x: `${50 + (Math.random() - 0.5) * 150}%`,
                                y: `${50 + (Math.random() - 0.5) * 150}%`,
                                scale: [0, 1.5, 0],
                                opacity: [1, 1, 0],
                                rotate: [0, 360]
                              }}
                              transition={{
                                duration: 1.5,
                                delay: i * 0.05,
                                repeat: Infinity
                              }}
                              className="absolute text-3xl"
                            >
                              {['⭐', '✨', '🎊', '🎉', '💫'][i % 5]}
                            </motion.div>
                          ))}
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Lion Roaring Animation Above Question Card */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0, y: -100 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0, y: -100 }}
                    transition={{ type: "spring", duration: 0.8 }}
                    className="fixed top-24 left-1/2 -translate-x-1/2 z-50 pointer-events-none"
                  >
                    <motion.div
                      animate={{
                        scale: [1, 1.3, 1],
                        rotate: [0, 10, -10, 0]
                      }}
                      transition={{
                        duration: 0.5,
                        repeat: Infinity,
                        repeatDelay: 1.5
                      }}
                      className="relative"
                    >
                      {/* Lion Character */}
                      <div className="text-8xl filter drop-shadow-2xl">
                        {isCorrect ? '🦁' : '😿'}
                      </div>
                      
                      {/* Roar Effect */}
                      {isCorrect && (
                        <motion.div
                          initial={{ scale: 0, opacity: 1 }}
                          animate={{ scale: [1, 2, 3], opacity: [1, 0.5, 0] }}
                          transition={{ duration: 1, repeat: Infinity }}
                          className="absolute inset-0 flex items-center justify-center"
                        >
                          {[...Array(3)].map((_, i) => (
                            <motion.div
                              key={i}
                              initial={{ scale: 0, opacity: 1 }}
                              animate={{ scale: [0, 1.5, 2.5], opacity: [1, 0.5, 0] }}
                              transition={{
                                duration: 1,
                                delay: i * 0.3,
                                repeat: Infinity
                              }}
                              className="absolute w-32 h-32 border-4 border-yellow-400 rounded-full"
                            />
                          ))}
                        </motion.div>
                      )}

                      {/* Sound Waves */}
                      {isCorrect && (
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                          {[...Array(5)].map((_, i) => (
                            <motion.div
                              key={i}
                              initial={{ scale: 0, opacity: 1, y: 0 }}
                              animate={{ 
                                scale: [0, 1, 1.5], 
                                opacity: [1, 0.5, 0],
                                y: [-20, -40, -60]
                              }}
                              transition={{
                                duration: 0.8,
                                delay: i * 0.15,
                                repeat: Infinity
                              }}
                              className="absolute text-2xl"
                              style={{ left: `${(i - 2) * 20}px` }}
                            >
                                💬
                              </motion.div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  </motion.div>

                  {/* Mascot Animation */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5, y: 50 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.5, y: 50 }}
                    transition={{ type: "spring", duration: 0.5 }}
                    className="mt-6"
                  >
                    <div className={`bg-gradient-to-r ${
                      isCorrect ? 'from-green-400 to-emerald-500' : 'from-orange-400 to-red-500'
                    } rounded-2xl p-6 shadow-xl`}>
                      <div className="flex items-start gap-4">
                        {/* Animated Lion Mascot Character */}
                        <motion.div
                          animate={{
                            scale: [1, 1.15, 1],
                            rotate: [0, 8, -8, 0]
                          }}
                          transition={{
                            duration: 0.6,
                            repeat: Infinity,
                            repeatDelay: 0.8
                          }}
                          className="flex-shrink-0"
                        >
                          <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg border-4 border-yellow-300">
                            <motion.div
                              animate={{
                                y: [0, -8, 0],
                                rotate: [0, 5, -5, 0]
                              }}
                              transition={{
                                duration: 1.2,
                                repeat: Infinity,
                                ease: "easeInOut"
                              }}
                              className="text-5xl"
                            >
                              {isCorrect ? '�' : '😿'}
                            </motion.div>
                          </div>
                          {/* Crown for correct answers */}
                          {isCorrect && (
                            <motion.div
                              initial={{ y: -20, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              transition={{ delay: 0.3 }}
                              className="absolute -top-6 left-1/2 -translate-x-1/2 text-3xl"
                            >
                              👑
                            </motion.div>
                          )}
                        </motion.div>

                        {/* Speech Bubble */}
                        <div className="flex-1">
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                            className="bg-white rounded-2xl p-5 shadow-lg relative"
                          >
                            {/* Speech bubble tail */}
                            <div className={`absolute left-0 top-1/2 -translate-x-2 -translate-y-1/2 w-4 h-4 ${
                              isCorrect ? 'bg-green-400' : 'bg-orange-400'
                            } transform rotate-45`} />
                            
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.3 }}
                              className="relative z-10"
                            >
                              <p className={`font-bold text-2xl mb-2 ${
                                isCorrect ? 'text-green-600' : 'text-orange-600'
                              }`}>
                                {isCorrect ? 'ROAR-some! 🦁' : 'Not quite! 😿'}
                              </p>
                              <p className="text-gray-700 text-xl leading-relaxed">
                                {mascotMessage}
                              </p>
                              <p className="text-gray-600 mt-3 text-base">
                                {currentQ.explanation}
                              </p>
                            </motion.div>
                          </motion.div>
                        </div>
                      </div>

                      {/* Animated particles and sparkles */}
                      {isCorrect && (
                        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
                          {[...Array(12)].map((_, i) => (
                            <motion.div
                              key={i}
                              initial={{ 
                                x: '50%', 
                                y: '50%', 
                                scale: 0,
                                opacity: 1
                              }}
                              animate={{
                                x: `${50 + (Math.random() - 0.5) * 120}%`,
                                y: `${50 + (Math.random() - 0.5) * 120}%`,
                                scale: [0, 1.5, 0],
                                opacity: [1, 1, 0],
                                rotate: [0, 360]
                              }}
                              transition={{
                                duration: 1.2,
                                delay: i * 0.08,
                                repeat: Infinity,
                                repeatDelay: 2.5
                              }}
                              className="absolute"
                            >
                              <span className="text-2xl">✨</span>
                            </motion.div>
                          ))}
                          {/* Stars */}
                          {[...Array(8)].map((_, i) => (
                            <motion.div
                              key={`star-${i}`}
                              initial={{ 
                                x: '50%', 
                                y: '50%', 
                                scale: 0,
                                opacity: 1
                              }}
                              animate={{
                                x: `${50 + (Math.random() - 0.5) * 100}%`,
                                y: `${50 + (Math.random() - 0.5) * 100}%`,
                                scale: [0, 1, 0],
                                opacity: [1, 1, 0]
                              }}
                              transition={{
                                duration: 1,
                                delay: i * 0.12,
                                repeat: Infinity,
                                repeatDelay: 2
                              }}
                              className="absolute text-3xl"
                            >
                              ⭐
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>

                  {/* XP Gain Animation with more effects */}
                  {isCorrect && (
                    <motion.div
                      initial={{ opacity: 0, y: 30, scale: 0.5 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -30, scale: 1.5 }}
                      className="mt-4 text-center"
                    >
                      <motion.div
                        animate={{
                          scale: [1, 1.1, 1],
                          rotate: [0, 5, -5, 0]
                        }}
                        transition={{
                          duration: 0.5,
                          repeat: Infinity,
                          repeatDelay: 1
                        }}
                        className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-full shadow-2xl"
                      >
                        <motion.span
                          animate={{ rotate: [0, 360] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          <FaStar className="text-yellow-300 text-2xl" />
                        </motion.span>
                        <span className="font-bold text-2xl">+10 XP</span>
                        <span className="text-xl">🎉</span>
                      </motion.div>
                    </motion.div>
                  )}

                  {/* Streak Bonus Animation */}
                  {isCorrect && streak > 0 && streak % 5 === 0 && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0 }}
                      className="mt-3 text-center"
                    >
                      <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-2 rounded-full shadow-lg">
                        <FaFire className="text-yellow-300" />
                        <span className="font-bold">🔥 {streak} Streak Bonus! +5 XP</span>
                      </div>
                    </motion.div>
                  )}
                </>
              )}
            </AnimatePresence>
          </motion.div>

          <AnimatePresence>
            {showFeedback && (
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                onClick={handleNext}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 rounded-xl font-semibold hover:opacity-90 transition-all flex items-center justify-center gap-2"
              >
                {currentQuestion < questions.length - 1 ? (
                  <>
                    Continue <FaArrowRight />
                  </>
                ) : (
                  <>
                    Complete Lesson <FaTrophy />
                  </>
                )}
              </motion.button>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default LXDLesson;
